import LegalPageLayout from '@/components/layout/LegalPageLayout';

const tocItems = [
  { id: 'overview', label: 'Security Overview' },
  { id: 'encryption', label: 'Encryption' },
  { id: 'infrastructure', label: 'Infrastructure' },
  { id: 'access', label: 'Access Control' },
  { id: 'monitoring', label: 'Monitoring & Logging' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'incident', label: 'Incident Response' },
  { id: 'best-practices', label: 'User Best Practices' },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="reveal py-10 border-b border-white/[0.04]">
      <h2 className="text-[22px] font-semibold tracking-tight leading-snug mb-5 text-white scroll-mt-[110px]">
        {title}
      </h2>
      <div className="text-sm text-zinc-500 leading-[1.75]">{children}</div>
    </section>
  );
}

function InfoCard({ children, accent, warn }) {
  let border = 'border-white/[0.06]';
  let bg = 'bg-white/[0.02]';
  if (accent) { border = 'border-white/[0.12]'; bg = 'bg-white/[0.04]'; }
  if (warn) { border = 'border-yellow-500/20'; bg = 'bg-yellow-500/[0.04]'; }
  return (
    <div className={`rounded-2xl border ${border} ${bg} p-5 my-5 transition-colors hover:border-white/[0.12]`}>
      {children}
    </div>
  );
}

export default function SecurityPage() {
  return (
    <LegalPageLayout
      title="Security"
      subtitle="ScheduleX is built with security at its core. Learn how we protect your data and keep your meetings safe."
      tocItems={tocItems}
    >
      <Section id="overview" title="Security Overview">
        <p className="mb-4">
          At ScheduleX, security is not an afterthought, it is foundational to everything we build. We employ industry-leading practices to ensure your scheduling data, personal information, and communications remain protected at all times.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-2">Our Commitment</p>
          <p>We continuously evaluate and improve our security posture. Our systems are regularly audited, and we maintain a responsible disclosure program for security researchers.</p>
        </InfoCard>
      </Section>

      <Section id="encryption" title="Encryption">
        <p className="mb-4">
          All data transmitted between your browser and our servers is encrypted using industry-standard protocols. Sensitive data at rest is encrypted using AES-256.
        </p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span><strong className="text-zinc-400">TLS 1.3</strong> — All network traffic is encrypted in transit</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span><strong className="text-zinc-400">AES-256</strong> — Sensitive data encrypted at rest</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span><strong className="text-zinc-400">bcrypt</strong> — Passwords hashed with a cost factor of 12</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span><strong className="text-zinc-400">JWT</strong> — Signed tokens for authenticated sessions</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="infrastructure" title="Infrastructure">
        <p className="mb-4">
          Our infrastructure is hosted on enterprise-grade cloud providers with multiple layers of physical and network security.
        </p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>DDoS protection and Web Application Firewall (WAF)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Automated security patching and vulnerability scanning</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Network segmentation and private subnets for databases</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Regular automated backups with point-in-time recovery</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="access" title="Access Control">
        <p className="mb-4">
          We implement strict access controls to ensure that only authorized personnel can access production systems and customer data.
        </p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Role-based access control (RBAC) for all internal systems</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Multi-factor authentication (MFA) required for all staff</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Principle of least privilege — employees only access what they need</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Regular access reviews and automated deprovisioning</span>
            </li>
          </ul>
        </InfoCard>
      </Section>

      <Section id="monitoring" title="Monitoring & Logging">
        <p>
          We maintain comprehensive logging and monitoring to detect and respond to security events in real time.
        </p>
        <InfoCard accent>
          <p className="font-medium text-white mb-1">Audit Trail</p>
          <p>All administrative actions are logged with timestamps, IP addresses, and user identifiers. Logs are retained for 90 days and are tamper-resistant.</p>
        </InfoCard>
      </Section>

      <Section id="compliance" title="Compliance">
        <p className="mb-4">
          We adhere to internationally recognized standards and best practices for data protection and security.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-5">
          {['SOC 2 Type II', 'GDPR Ready', 'CCPA Compliant', 'ISO 27001'].map((tag) => (
            <div key={tag} className="px-3 py-2.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-xs text-zinc-400 font-medium text-center">
              {tag}
            </div>
          ))}
        </div>
        <InfoCard>
          <p className="font-medium text-white mb-1">Data Residency</p>
          <p>Customer data is stored in the region you select during account creation. We do not transfer data across regions without explicit consent.</p>
        </InfoCard>
      </Section>

      <Section id="incident" title="Incident Response">
        <p>
          We have a documented incident response plan to address security events promptly and transparently.
        </p>
        <InfoCard warn>
          <p className="font-medium text-yellow-400 mb-1">Reporting</p>
          <p>If you discover a security vulnerability, please report it to <a href="mailto:security@schedulex.site" className="text-white hover:underline">security@schedulex.site</a>. We commit to acknowledging reports within 24 hours and resolving critical issues within 72 hours.</p>
        </InfoCard>
      </Section>

      <Section id="best-practices" title="User Best Practices">
        <p className="mb-4">
          We recommend the following practices to keep your ScheduleX account secure:
        </p>
        <InfoCard>
          <ul className="space-y-2">
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Use a strong, unique password for your ScheduleX account</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Enable two-factor authentication (2FA) in your account settings</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Regularly review your active sessions and revoke unused ones</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Be cautious of phishing emails claiming to be from ScheduleX</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-zinc-600 mt-2 shrink-0" />
              <span>Keep your browser and operating system up to date</span>
            </li>
          </ul>
        </InfoCard>
      </Section>
    </LegalPageLayout>
  );
}
